import React from "react";
import { useMutation } from '@tanstack/react-query';
import { Tooltip, toast, ToastAction } from "@altimateai/lego";
import { LoadingButton } from "@altimateai/lego";
import { JiraIcon } from "@/assets/icons";
import { createJiraTicket } from "@/helpers/apis";
import { useAppState } from "../app/useAppContext";

interface JiraTicketButtonProps {
  opportunityId: string;
  queryId?: string;
}

const JiraTicketButton: React.FC<JiraTicketButtonProps> = ({ opportunityId, queryId }) => {
  const { jiraTicketEnabled } = useAppState();
  const { mutate: jiraTicketMutation, isPending: isCreatingTicket } = useMutation({
    mutationFn: (variables: { opportunityId: string; queryId?: string }) =>
      createJiraTicket(variables.opportunityId, variables.queryId ?? null),
    onSuccess: data => {
      toast({
        title: `Successfully created Jira ticket`,
        description: `Jira ID: ${data?.issue_key}`,
        action: (
          <ToastAction
            altText="Copy URL"
            onClick={async () => await navigator.clipboard.writeText(data?.url ?? "")}
          >
            Copy URL
          </ToastAction>
        ),
        variant: "default",
      });
    },
    onError: error => {
      toast({
        title: "Failed to create Jira ticket",
        description: String(error),
        variant: "destructive",
      });
    },
  });

  const handleCreateJiraTicket = (opportunityId: string) => {
    jiraTicketMutation({ opportunityId, queryId });
  };

  if (!jiraTicketEnabled) {
    return null;
  }

  return (
    <Tooltip title="Create Jira Ticket">
      <LoadingButton
        variant="outline"
        size="icon"
        title="Create Jira Ticket"
        loading={isCreatingTicket}
        onClick={event => {
          event.stopPropagation();
          handleCreateJiraTicket(opportunityId);
        }}
      >
        <JiraIcon />
      </LoadingButton>
    </Tooltip>
  );
};

export default JiraTicketButton;
